import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// project imports
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';
import useOrgBranding from 'hooks/useOrgBranding';
import { applyBranding } from './applyBranding';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig();

  const theme = useMemo(() => Palette(mode, presetColor), [mode, presetColor]);

  const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);

  // The signed-in organization's Advanced Settings → Branding, layered over the
  // base theme. Null for platform accounts and for any organization that has not
  // defined its own, which is why the default look is unchanged for them.
  const branding = useOrgBranding();

  const brandedPalette = useMemo(() => applyBranding(theme.palette, branding), [theme.palette, branding]);

  const brandedTypography = useMemo(
    () =>
      branding?.font_family ? { ...themeTypography, fontFamily: `"${branding.font_family}", system-ui, sans-serif` } : themeTypography,
    [themeTypography, branding]
  );

  // Shadows derive from the palette, so they follow the branded one — otherwise
  // an organization's primary colour and its shadows would disagree.
  const themeCustomShadows = useMemo(() => CustomShadows({ ...theme, palette: brandedPalette }), [theme, brandedPalette]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      palette: brandedPalette,
      customShadows: themeCustomShadows,
      typography: brandedTypography
    }),
    [themeDirection, brandedPalette, brandedTypography, themeCustomShadows]
  );

  const themes = useMemo(() => {
    const t = createTheme(themeOptions);
    t.components = componentsOverride(t);
    return t;
  }, [themeOptions]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
